// @ts-nocheck -- staged migration: add explicit types before removing this directive.
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faChevronDown, faChevronUp } from "@fortawesome/free-solid-svg-icons";
import Header from "../header/HeaderComponent";
import Footer from "../footer/FooterComponent";
import { useJlptLevel } from "../../hooks/useJlptLevel";
import { fetchVocabularyLevel, wordMatchesMeaning, wordMatchesRomaji } from "../../utils/vocabularyData";
import { conjugateVerb, MASHOUKA_LABEL, TO_OMOIMASU_LABEL, verbGroupOf } from "../../utils/verbConjugation";
import { conjugationRomaji } from "../../utils/conjugationRomaji";
import { teIruFormsFrom, teIruNoteFor } from "../../utils/teIruConjugation";
import { kanaToRomaji } from "../../utils/kanaToRomaji";
import { useConjugationProgress } from "../../hooks/useConjugationProgress";
import { useConjugationListenCounts } from "../../hooks/useConjugationListenCounts";
import { useAuth } from "../../context/AuthContext";
import { LearnedBadge, LearningStatusButton } from "../LearningStatus/LearningStatus";
import SpeakButton from "../SpeakButton/SpeakButtonComponent";
import Seo from "../Seo/SeoComponent";
import TeIruGuide, { JA_TRANS_COMPONENTS } from "./TeIruGuide";
import UsuallyKanaWord from "../UsuallyKanaWord/UsuallyKanaWord";
import styles from "./conjugationPage.module.scss";
import { useLocalizedPath } from "../../i18n/localizedPath";
import { isConjugationPageVerb } from "../../consts/verbExceptions";

const levelKeys = ["5", "4", "3", "2", "1"];

// A filter value of its own, not a JLPT level: shows every level's verbs at
// once and, like any manual chip pick, stops the saved JLPT preference from
// choosing the filter, so it doubles as "reset to no filter".
const ALL_LEVELS = "all";

// The verb-group filter's own "no filter" sentinel, independent of ALL_LEVELS
// even though both happen to be the string "all" - they filter different axes.
const ALL_GROUPS = "all";
const GROUP_FILTER_KEYS = ["godan", "ichidan", "irregular"];

// Every card renders its full conjugation table, so a long list is expensive:
// measured in dev, all ~2,700 verbs took ~47s to render and ~27s to filter.
// Cards are shown in batches instead; searching still covers every verb.
const VERBS_PER_BATCH = 60;

// How long the search box must sit unchanged on an empty result before other
// levels are fetched, so a learner mid-word never triggers a download.
const CROSS_LEVEL_SEARCH_DELAY_MS = 600;

function verbMatchesQuery(w, trimmed) {
    return (
        w.word.includes(trimmed) ||
        w.alsoWritten?.toLowerCase().includes(trimmed) ||
        w.reading.includes(trimmed) ||
        wordMatchesRomaji(w, trimmed) ||
        wordMatchesMeaning(w, trimmed)
    );
}

// The PT-PT blog twin is a standalone post under /blog/ (not /pt/blog/), so the
// target is picked by language rather than run through localizePath.
const VERB_GROUPS_BLOG_PATH = {
    en: "/blog/godan-vs-ichidan-japanese-verb-groups",
    pt: "/blog/verbos-godan-e-ichidan-grupos-de-verbos-em-japones",
};

const VERB_TYPE_I18N_KEYS = {
    v1: "ichidan",
    "v1-s": "ichidan",
    vz: "ichidanZuru",
    vk: "kuru",
    vs: "suru",
    "vs-i": "suru",
    "vs-c": "suru",
    "vs-s": "suru",
};

// Phrase-pattern forms are shown with a translated label and a short note
// under their row, looked up in the "forms" namespace. Every other form keeps
// its English label from verbConjugation.ts (the listen-count key uses it too).
const PHRASE_FORM_I18N_KEYS = {
    [MASHOUKA_LABEL]: "mashouka",
    [TO_OMOIMASU_LABEL]: "toOmoimasu",
};

function describeVerbType(verbType, t) {
    if (VERB_TYPE_I18N_KEYS[verbType]) return t(`verbTypes.${VERB_TYPE_I18N_KEYS[verbType]}`);
    // Every tag VERB_TYPE_I18N_KEYS doesn't already name explicitly is either
    // godan (verbGroupOf's startsWith("v5") branch) or a class this app has no
    // name for at all - sharing verbGroupOf here keeps this in lockstep with
    // the group filter below instead of re-deriving "is this godan?" twice.
    if (verbGroupOf(verbType) === "godan") return t("verbTypes.godan");
    return t("verbTypes.verb");
}

// A compact, unambiguous "Group N" badge for a verb card, independent of the
// descriptive POS badge above (which already says e.g. "Suru verb" or "Kuru
// verb (irregular)") - this is the one spot a learner can read the group
// without inferring it from the word's own kana ending.
function describeVerbGroup(verbType, t) {
    const group = verbGroupOf(verbType);
    return group ? t(`groupBadge.${group}`) : null;
}

function ConjugationTable({
    entryId,
    word,
    reading,
    verbType,
    listenCounts,
    isListenProgressReady,
    isAuthed,
    onListen,
}) {
    const { t } = useTranslation("conjugationPage");
    const forms = useMemo(() => conjugateVerb(word, reading, verbType), [word, reading, verbType]);
    // Built from the te-form row of `forms` itself, so the two cannot disagree.
    const teIruForms = useMemo(() => teIruFormsFrom(forms), [forms]);
    const teIruNote = teIruNoteFor(word, reading);
    const teIruHeadingId = useId();
    // Each row's romaji is revealed independently, rather than one toggle
    // for the whole table - keyed by form label, which is unique within
    // this one verb's conjugation set.
    const [romajiVisible, setRomajiVisible] = useState(() => new Set());

    function toggleRomaji(formLabel) {
        setRomajiVisible((current) => {
            const next = new Set(current);
            if (next.has(formLabel)) next.delete(formLabel);
            else next.add(formLabel);
            return next;
        });
    }

    if (!forms) return null;

    // One row of the table: used for the existing forms and the ている group alike,
    // so the romaji reveal, the listen buttons and their counts behave identically.
    // labelNode replaces the plain-text row heading (the ている rows keep their
    // Japanese ending in its own lang="ja" span); a note adds a line under the row.
    function renderRow(form, formLabel, { labelNode, note }) {
        const listenKey = `${entryId}:${form.label}`;
        const listenCount = listenCounts[listenKey] || 0;
        const isRomajiVisible = romajiVisible.has(form.label);
        return (
            <React.Fragment key={form.label}>
                <tr>
                    <th scope="row" className={note || labelNode ? styles.conjugationFormLabelWrap : undefined}>
                        {labelNode || formLabel}
                    </th>
                    <td lang="ja" className={styles.conjugationKanji}>
                        {form.kanji}
                    </td>
                    <td lang="ja" className={styles.conjugationReadingCell}>
                        {form.reading}
                    </td>
                    <td className={styles.conjugationRomajiCell}>
                        <button
                            type="button"
                            className={
                                isRomajiVisible
                                    ? styles.romajiReveal
                                    : `${styles.romajiReveal} ${styles.romajiRevealMasked}`
                            }
                            onClick={() => toggleRomaji(form.label)}
                            aria-pressed={isRomajiVisible}
                            aria-label={
                                isRomajiVisible
                                    ? t("table.hideRomaji", { form: formLabel, word: form.kanji })
                                    : t("table.showRomaji", { form: formLabel, word: form.kanji })
                            }
                        >
                            {conjugationRomaji(form)}
                        </button>
                    </td>
                    <td className={styles.conjugationListenCell}>
                        <SpeakButton
                            text={form.reading}
                            label={t("table.playPronunciation", { form: formLabel, word: form.kanji })}
                            className={styles.conjugationSpeakButton}
                            onPlay={() => onListen(listenKey)}
                        />
                        <SpeakButton
                            text={form.reading}
                            label={t("table.playSlowPronunciation", { form: formLabel, word: form.kanji })}
                            className={styles.conjugationSpeakButton}
                            onPlay={() => onListen(listenKey)}
                            slow
                        />
                        {isAuthed && isListenProgressReady && listenCount > 0 && (
                            <span
                                className={styles.conjugationListenBadge}
                                aria-label={t("table.listened", { count: listenCount })}
                            >
                                {listenCount}×
                            </span>
                        )}
                    </td>
                </tr>
                {note && (
                    <tr>
                        <td colSpan={5} className={styles.conjugationFormNoteCell}>
                            <p className={styles.conjugationFormNote}>{note}</p>
                        </td>
                    </tr>
                )}
            </React.Fragment>
        );
    }

    return (
        <div className={styles.conjugationTableScroll}>
            <table className={styles.conjugationTable}>
                <thead>
                    <tr>
                        <th scope="col">{t("table.form")}</th>
                        <th scope="col">{t("table.word")}</th>
                        <th scope="col">{t("table.reading")}</th>
                        <th scope="col">{t("table.romaji")}</th>
                        <th scope="col" className={styles.conjugationListenHeader}>
                            {t("table.listen")}
                        </th>
                    </tr>
                </thead>
                <tbody>
                    {forms.map((form) => {
                        const phraseFormKey = PHRASE_FORM_I18N_KEYS[form.label];
                        const formLabel = phraseFormKey ? t(`forms.${phraseFormKey}.label`) : form.label;
                        return renderRow(form, formLabel, {
                            note: phraseFormKey ? t(`forms.${phraseFormKey}.note`) : null,
                        });
                    })}
                </tbody>
                {teIruForms && (
                    <tbody aria-labelledby={teIruHeadingId}>
                        <tr>
                            <th colSpan={5} scope="rowgroup" className={styles.teIruGroupHeading}>
                                <span id={teIruHeadingId} className={styles.teIruGroupTitle}>
                                    <Trans t={t} i18nKey="teIru.groupTitle" values={{ word }} components={JA_TRANS_COMPONENTS} />
                                </span>
                                <span className={styles.teIruGroupHint}>{t("teIru.groupHint")}</span>
                            </th>
                        </tr>
                        {teIruForms.map((form) => {
                            const meaning = t(`teIru.forms.${form.key}`);
                            return renderRow(form, `${form.pattern} — ${meaning}`, {
                                labelNode: (
                                    <>
                                        <span lang="ja" className={styles.teIruPattern}>
                                            {form.pattern}
                                        </span>
                                        {` — ${meaning}`}
                                    </>
                                ),
                            });
                        })}
                        {teIruNote && (
                            <tr>
                                <td colSpan={5} className={styles.conjugationFormNoteCell}>
                                    <p className={`${styles.conjugationFormNote} ${styles.teIruNote}`}>
                                        <Trans
                                            t={t}
                                            i18nKey={`teIru.notes.${teIruNote}`}
                                            components={JA_TRANS_COMPONENTS}
                                        />
                                    </p>
                                </td>
                            </tr>
                        )}
                    </tbody>
                )}
            </table>
        </div>
    );
}

function VerbCard({
    entry,
    learned,
    expanded,
    isProgressReady,
    onToggleLearned,
    onToggleExpanded,
    listenCounts,
    isListenProgressReady,
    isAuthed,
    onListen,
}) {
    const { t } = useTranslation("conjugationPage");
    const groupLabel = describeVerbGroup(entry.verbType, t);

    return (
        <li className={`${styles.verbCard} ${learned ? styles.verbCardLearned : ""}`}>
            <div className={styles.verbCardHeader}>
                <div className={styles.verbCardText}>
                    <div className={styles.verbWordRow}>
                        <UsuallyKanaWord className={styles.verbWord} word={entry.word} alsoWritten={entry.alsoWritten} />
                        {entry.word !== entry.reading && (
                            <span className={styles.verbReading}>
                                {entry.reading} ({kanaToRomaji(entry.reading)})
                            </span>
                        )}
                        <SpeakButton
                            text={entry.reading}
                            label={t("playWordPronunciation", { word: entry.word })}
                            className={styles.verbWordSpeakButton}
                        />
                        <SpeakButton
                            text={entry.reading}
                            label={t("playWordSlowPronunciation", { word: entry.word })}
                            className={styles.verbWordSpeakButton}
                            slow
                        />
                    </div>
                    <p className={styles.verbMeaning}>{entry.meanings.join(", ")}</p>
                    <div className={styles.badgeRow}>
                        <span className={styles.posBadge}>{describeVerbType(entry.verbType, t)}</span>
                        {groupLabel && <span className={styles.groupBadge}>{groupLabel}</span>}
                        {learned && <LearnedBadge />}
                    </div>
                </div>
                <LearningStatusButton
                    learned={learned}
                    disabled={!isProgressReady}
                    onClick={() => onToggleLearned(entry.id, !learned)}
                />
            </div>

            <button type="button" className={styles.conjugationToggle} onClick={() => onToggleExpanded(entry.id)}>
                <FontAwesomeIcon icon={expanded ? faChevronUp : faChevronDown} />
                {expanded ? t("hideConjugations") : t("showConjugations")}
            </button>
            {expanded && (
                <ConjugationTable
                    entryId={entry.id}
                    word={entry.word}
                    reading={entry.reading}
                    verbType={entry.verbType}
                    listenCounts={listenCounts}
                    isListenProgressReady={isListenProgressReady}
                    isAuthed={isAuthed}
                    onListen={onListen}
                />
            )}
        </li>
    );
}

function ConjugationPage() {
    const { t, i18n } = useTranslation("conjugationPage");
    const lp = useLocalizedPath();
    const { status: authStatus } = useAuth();
    const isAuthed = authStatus === "authenticated";
    const { level: preferredLevel, syncStatus: levelSyncStatus } = useJlptLevel();
    const [level, setLevel] = useState(() => preferredLevel || "5");
    const [groupFilter, setGroupFilter] = useState(ALL_GROUPS);
    const [query, setQuery] = useState("");
    const [learnedExpanded, setLearnedExpanded] = useState(false);
    const [expandedCards, setExpandedCards] = useState({});
    const [visibleCount, setVisibleCount] = useState(VERBS_PER_BATCH);
    const userChangedLevelRef = useRef(false);
    const { progress, setLearned, syncStatus } = useConjugationProgress();
    const isProgressReady = syncStatus !== "loading";
    const {
        counts: listenCounts,
        increment: incrementListenCount,
        syncStatus: listenSyncStatus,
    } = useConjugationListenCounts();
    const isListenProgressReady = listenSyncStatus !== "loading";

    const [words, setWords] = useState([]);
    const [loadState, setLoadState] = useState("loading"); // loading | ready | error

    const isPt = i18n.language === "pt";
    const currentPath = isPt ? "/pt/conjugation" : "/conjugation";

    useEffect(() => {
        if (userChangedLevelRef.current || levelSyncStatus === "loading") return;
        if (preferredLevel) setLevel(preferredLevel);
    }, [preferredLevel, levelSyncStatus]);

    useEffect(() => {
        let cancelled = false;
        setLoadState("loading");
        // Word ids are unique across levels, so the lists can simply be joined
        // (easiest level first); each level file is cached after its first fetch.
        const levelsToLoad = level === ALL_LEVELS ? levelKeys : [level];
        Promise.all(levelsToLoad.map((key) => fetchVocabularyLevel(key, i18n.language)))
            .then((lists) => {
                if (cancelled) return;
                setWords(lists.flat());
                setLoadState("ready");
            })
            .catch(() => {
                if (!cancelled) setLoadState("error");
            });
        return () => {
            cancelled = true;
        };
    }, [level, i18n.language]);

    function handleLevelChange(key) {
        userChangedLevelRef.current = true;
        setLevel(key);
        setVisibleCount(VERBS_PER_BATCH);
    }

    function handleGroupFilterChange(key) {
        setGroupFilter(key);
        setVisibleCount(VERBS_PER_BATCH);
    }

    function showMoreVerbs() {
        setVisibleCount((count) => count + VERBS_PER_BATCH);
    }

    const verbs = useMemo(() => words.filter(isConjugationPageVerb), [words]);

    const groupFilteredVerbs = useMemo(() => {
        if (groupFilter === ALL_GROUPS) return verbs;
        return verbs.filter((w) => verbGroupOf(w.verbType) === groupFilter);
    }, [verbs, groupFilter]);

    const filteredVerbs = useMemo(() => {
        const trimmed = query.trim().toLowerCase();
        if (!trimmed) return groupFilteredVerbs;
        return groupFilteredVerbs.filter((w) => verbMatchesQuery(w, trimmed));
    }, [groupFilteredVerbs, query]);

    // No-results fallback. Step 1 (free): the match is hidden only by the group
    // filter. Step 2 (fetches): the match is in another JLPT level's file.
    const trimmedQuery = query.trim().toLowerCase();
    const isEmptyResult = loadState === "ready" && trimmedQuery !== "" && filteredVerbs.length === 0;
    const hiddenByGroupCount = useMemo(() => {
        if (!isEmptyResult || groupFilter === ALL_GROUPS) return 0;
        return verbs.filter((w) => verbMatchesQuery(w, trimmedQuery)).length;
    }, [isEmptyResult, groupFilter, verbs, trimmedQuery]);
    // { status: "searching" | "found" | "none", level?, count?, forQuery }
    const [elsewhere, setElsewhere] = useState(null);

    useEffect(() => {
        if (!isEmptyResult || hiddenByGroupCount > 0 || level === ALL_LEVELS) {
            setElsewhere(null);
            return undefined;
        }
        let cancelled = false;
        setElsewhere(null);
        const timer = setTimeout(async () => {
            setElsewhere({ status: "searching" });
            // Easiest level first, stopping at the first level with a hit: N5 is
            // the likeliest home of a beginner's search, and a miss costs one
            // small cached file at a time rather than all four.
            try {
                for (const key of levelKeys) {
                    if (key === level) continue;
                    const list = await fetchVocabularyLevel(key, i18n.language);
                    if (cancelled) return;
                    const hits = list
                        .filter(isConjugationPageVerb)
                        .filter((w) => groupFilter === ALL_GROUPS || verbGroupOf(w.verbType) === groupFilter)
                        .filter((w) => verbMatchesQuery(w, trimmedQuery));
                    if (hits.length > 0) {
                        setElsewhere({ status: "found", level: key, count: hits.length, example: hits[0] });
                        return;
                    }
                }
                setElsewhere({ status: "none" });
            } catch {
                // Offline or a bad file: fall back to the plain "no matches" text.
                if (!cancelled) setElsewhere({ status: "none" });
            }
        }, CROSS_LEVEL_SEARCH_DELAY_MS);
        return () => {
            cancelled = true;
            clearTimeout(timer);
        };
    }, [isEmptyResult, hiddenByGroupCount, level, groupFilter, trimmedQuery, i18n.language]);

    function showFoundLevel(key) {
        handleLevelChange(key);
    }

    const { learningVerbs, learnedVerbs } = useMemo(() => {
        const learning = [];
        const learned = [];
        filteredVerbs.forEach((verb) => {
            if (isProgressReady && progress[verb.id] === "learned") learned.push(verb);
            else learning.push(verb);
        });
        return { learningVerbs: learning, learnedVerbs: learned };
    }, [filteredVerbs, isProgressReady, progress]);

    function toggleCardExpansion(entryId) {
        setExpandedCards((current) => ({
            ...current,
            [entryId]: current[entryId] === undefined ? false : !current[entryId],
        }));
    }

    function renderVerbCard(entry) {
        return (
            <VerbCard
                key={entry.id}
                entry={entry}
                learned={isProgressReady && progress[entry.id] === "learned"}
                expanded={expandedCards[entry.id] !== false}
                isProgressReady={isProgressReady}
                onToggleLearned={setLearned}
                onToggleExpanded={toggleCardExpansion}
                listenCounts={listenCounts}
                isListenProgressReady={isListenProgressReady}
                isAuthed={isAuthed}
                onListen={incrementListenCount}
            />
        );
    }

    function renderShowMore(total) {
        const remaining = total - visibleCount;
        if (remaining <= 0) return null;
        return (
            <button type="button" className={styles.showMoreButton} onClick={showMoreVerbs}>
                {t("showMoreVerbs", { count: Math.min(remaining, VERBS_PER_BATCH), remaining })}
            </button>
        );
    }

    return (
        <div className={styles.page}>
            <Seo
                title={t("seoTitle")}
                description={t("seoDescription")}
                path={currentPath}
                lang={isPt ? "pt-PT" : undefined}
            />
            <Header />
            <main className={styles.main}>
                <section className={styles.container}>
                    <h1 className={styles.title}>{t("title")}</h1>
                    <p className={styles.description}>{t("description")}</p>

                    <p className={styles.verbGroupsHelp}>
                        {t("verbGroupsHelp.intro")}{" "}
                        <Link to={lp("/lessons?lesson=11.5")}>{t("verbGroupsHelp.lesson")}</Link>
                        {" · "}
                        <Link to={lp("/grammar/n5-verb-groups")}>{t("verbGroupsHelp.grammar")}</Link>
                        {" · "}
                        <Link to={isPt ? VERB_GROUPS_BLOG_PATH.pt : VERB_GROUPS_BLOG_PATH.en}>
                            {t("verbGroupsHelp.blog")}
                        </Link>
                    </p>

                    <p className={styles.verbGroupsHelp}>
                        {t("adjectivesHelp.intro")} <Link to={lp("/adjective-conjugation")}>{t("adjectivesHelp.link")}</Link>
                    </p>

                    <TeIruGuide />

                    <div className={styles.card}>
                        <h2 className={styles.cardTitle}>{t("filterByLevel")}</h2>
                        <div className={styles.filterRow}>
                            {[ALL_LEVELS, ...levelKeys].map((key) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={key === level ? styles.levelChipActive : styles.levelChip}
                                    onClick={() => handleLevelChange(key)}
                                >
                                    {key === ALL_LEVELS ? t("allLevels") : `N${key}`}
                                </button>
                            ))}
                        </div>
                    </div>

                    <div className={styles.card}>
                        <h2 className={styles.cardTitle}>{t("filterByGroup")}</h2>
                        <div className={styles.filterRow}>
                            {[ALL_GROUPS, ...GROUP_FILTER_KEYS].map((key) => (
                                <button
                                    key={key}
                                    type="button"
                                    className={key === groupFilter ? styles.levelChipActive : styles.levelChip}
                                    onClick={() => handleGroupFilterChange(key)}
                                >
                                    {t(`groupFilter.${key}`)}
                                </button>
                            ))}
                        </div>
                    </div>

                    <input
                        type="search"
                        className={styles.searchInput}
                        placeholder={t("searchPlaceholder")}
                        value={query}
                        onChange={(event) => {
                            setQuery(event.target.value);
                            setVisibleCount(VERBS_PER_BATCH);
                        }}
                    />

                    {loadState === "loading" && (
                        <p className={styles.emptyText}>
                            {level === ALL_LEVELS ? t("loadingAllVerbs") : t("loadingVerbs", { level })}
                        </p>
                    )}
                    {loadState === "error" && (
                        <p className={styles.emptyText}>{t("loadError")}</p>
                    )}
                    {loadState === "ready" && (
                        <div role="status" aria-live="polite">
                            {isEmptyResult && hiddenByGroupCount > 0 && (
                                <p className={`${styles.emptyText} ${styles.elsewhereText}`}>
                                    {t("elsewhere.otherGroups", { count: hiddenByGroupCount })}{" "}
                                    <button
                                        type="button"
                                        className={styles.elsewhereButton}
                                        onClick={() => handleGroupFilterChange(ALL_GROUPS)}
                                    >
                                        {t("elsewhere.showAllGroups")}
                                    </button>
                                </p>
                            )}
                            {isEmptyResult && hiddenByGroupCount === 0 && elsewhere?.status === "searching" && (
                                <p className={`${styles.emptyText} ${styles.elsewhereText}`}>{t("elsewhere.searching", { level })}</p>
                            )}
                            {isEmptyResult && hiddenByGroupCount === 0 && elsewhere?.status === "found" && (
                                <p className={`${styles.emptyText} ${styles.elsewhereText}`}>
                                    {t("elsewhere.found", {
                                        level: elsewhere.level,
                                        current: level,
                                        count: elsewhere.count,
                                        word: elsewhere.example.word,
                                    })}{" "}
                                    <button
                                        type="button"
                                        className={styles.elsewhereButton}
                                        onClick={() => showFoundLevel(elsewhere.level)}
                                    >
                                        {t("elsewhere.showLevel", { level: elsewhere.level })}
                                    </button>
                                </p>
                            )}
                            {isEmptyResult &&
                                hiddenByGroupCount === 0 &&
                                (elsewhere === null || elsewhere.status === "none") && (
                                    <p className={styles.emptyText}>{t("noMatches")}</p>
                                )}
                            {!isEmptyResult && filteredVerbs.length === 0 && (
                                <p className={styles.emptyText}>{t("noMatches")}</p>
                            )}
                        </div>
                    )}

                    {loadState === "ready" && filteredVerbs.length > 0 && (
                        <>
                            {learningVerbs.length > 0 && (
                                <>
                                    <ul className={styles.verbList}>
                                        {learningVerbs.slice(0, visibleCount).map(renderVerbCard)}
                                    </ul>
                                    {renderShowMore(learningVerbs.length)}
                                </>
                            )}
                            {learnedVerbs.length > 0 && (
                                <section className={styles.learnedSection} aria-labelledby="learned-conjugations-heading">
                                    <div className={styles.learnedSectionHeader}>
                                        <h2 id="learned-conjugations-heading" className={styles.learnedSectionTitle}>
                                            {t("learnedConjugations", { count: learnedVerbs.length })}
                                        </h2>
                                        <button
                                            type="button"
                                            className={styles.learnedSectionToggle}
                                            aria-expanded={learnedExpanded}
                                            aria-controls="learned-conjugations-list"
                                            onClick={() => setLearnedExpanded((value) => !value)}
                                        >
                                            <FontAwesomeIcon icon={learnedExpanded ? faChevronUp : faChevronDown} />
                                            {learnedExpanded ? t("hideLearnedConjugations") : t("showLearnedConjugations")}
                                        </button>
                                    </div>
                                    <ul id="learned-conjugations-list" className={styles.verbList} hidden={!learnedExpanded}>
                                        {learnedVerbs.slice(0, visibleCount).map(renderVerbCard)}
                                    </ul>
                                    {learnedExpanded && renderShowMore(learnedVerbs.length)}
                                </section>
                            )}
                        </>
                    )}

                    <p className={styles.attribution}>
                        {t("attribution.wordsFrom")}{" "}
                        <a href="https://www.edrdg.org/wiki/index.php/JMdict-EDICT_Dictionary_Project" target="_blank" rel="noreferrer">
                            JMdict
                        </a>
                        {t("attribution.copyright")}{" "}
                        <a href="https://creativecommons.org/licenses/by-sa/4.0/" target="_blank" rel="noreferrer">
                            CC BY-SA 4.0
                        </a>{" "}
                        {t("attribution.licence")}{" "}
                        <a href="https://www.tanos.co.uk/jlpt/" target="_blank" rel="noreferrer">
                            Jonathan Waller's JLPT resources
                        </a>
                        {t("attribution.usedUnder")}{" "}
                        <a href="https://creativecommons.org/licenses/by/4.0/" target="_blank" rel="noreferrer">
                            CC BY 4.0
                        </a>{" "}
                        {t("attribution.licenceEnd")}
                    </p>
                </section>
            </main>
            <Footer />
        </div>
    );
}

export default ConjugationPage;
